import { useEffect, useRef } from 'react'

/**
 * Keeps the screen awake (Android Chrome / kiosk tablets).
 * Uses the Screen Wake Lock API when available.
 */
export function useScreenWakeLock(enabled: boolean) {
  const wakeLockRef = useRef<any>(null)

  useEffect(() => {
    if (!enabled) return

    const canUseWakeLock =
      typeof navigator !== 'undefined' && typeof (navigator as any).wakeLock?.request === 'function'

    if (!canUseWakeLock) return

    const release = async () => {
      try {
        await wakeLockRef.current?.release?.()
      } catch {
        // ignore
      } finally {
        wakeLockRef.current = null
      }
    }

    const request = async () => {
      try {
        wakeLockRef.current = await (navigator as any).wakeLock.request('screen')
      } catch {
        // This can fail if battery is low, permission denied, or OS policy blocks it.
        // We rely on the realtime watchdog + resume refetch as a backup.
      }
    }

    const sync = () => {
      if (document.visibilityState === 'visible') {
        void request()
      } else {
        void release()
      }
    }

    sync()
    document.addEventListener('visibilitychange', sync)

    return () => {
      document.removeEventListener('visibilitychange', sync)
      void release()
    }
  }, [enabled])
}

